import { useState, useEffect } from "react";
import { getAllSubscriptionTypes, createSubscriptionType, updateSubscriptionType, deleteSubscriptionType } from "../../services/AdminService";
import { PlusIcon, PencilIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/outline";

const SubscriptionTypeModal = ({ isOpen, onClose, onSubmit, title, isEdit = false, formData, handleChange }) => {
    if (!isOpen) return null;


    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold" style={{ fontFamily: 'Red Hat Display' }}>{title}</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <form onSubmit={onSubmit} className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium">Subscription Name</label>
                        <input
                            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            type="text"
                            placeholder="Premium Plan"
                            name="subscription_name"
                            value={formData.subscription_name || ''}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium">Description</label>
                        <textarea
                            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="Full access to all features"
                            name="description"
                            value={formData.description || ''}
                            onChange={handleChange}
                            rows={3}
                            required
                        />
                    </div>

                    <div className="flex flex-row gap-4">
                        <div className="flex flex-col w-1/2 gap-2">
                            <label className="text-sm font-medium">Price</label>
                            <input
                                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                type="number"
                                step="0.01"
                                placeholder="99.99"
                                name="price"
                                value={formData.price || ''}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="flex flex-col w-1/2 gap-2">
                            <label className="text-sm font-medium">Currency</label>
                            <select
                                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                name="currency"
                                value={formData.currency || ''}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select Currency</option>
                                <option value="USD">USD</option>
                                <option value="EUR">EUR</option>
                                <option value="GBP">GBP</option>
                                <option value="SGD">SGD</option>
                                <option value="IDR">IDR</option>
                            </select>
                        </div>
                    </div>

                    <div className="flex flex-row gap-4">
                        <div className="flex flex-col w-1/2 gap-2">
                            <label className="text-sm font-medium">Billing Frequency</label>
                            <select
                                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                name="billing_frequency"
                                value={formData.billing_frequency || ''}
                                onChange={handleChange}
                            >
                                <option value="">Select Frequency</option>
                                <option value="once">Once</option>
                                <option value="monthly">Monthly</option>
                                <option value="quarterly">Quarterly</option>
                                <option value="annual">Annual</option>
                            </select>
                        </div>
                        <div className="flex flex-col w-1/2 gap-2">
                            <label className="text-sm font-medium">Duration (Days) <span className="text-gray-500 text-xs">- OR use End Date below</span></label>
                            <input
                                className={`px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent ${formData.end_date ? 'border-gray-200 bg-gray-50 text-gray-400' : 'border-gray-300 focus:ring-orange-500'
                                    }`}
                                type="number"
                                placeholder="365"
                                name="duration_days"
                                value={formData.duration_days || ''}
                                onChange={handleChange}
                                disabled={!!formData.end_date}
                            />
                            {formData.end_date && (
                                <p className="text-xs text-gray-500">Disabled because End Date is set</p>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium">Features (comma-separated)</label>
                        <textarea
                            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="Feature 1, Feature 2, Feature 3"
                            name="features"
                            value={Array.isArray(formData.features) ? formData.features.join(', ') : formData.features || ''}
                            onChange={handleChange}
                            rows={2}
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium">End Date <span className="text-gray-500 text-xs">- OR use Duration above</span></label>
                        <input
                            className={`px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent ${formData.duration_days ? 'border-gray-200 bg-gray-50 text-gray-400' : 'border-gray-300 focus:ring-orange-500'
                                }`}
                            type="datetime-local"
                            name="end_date"
                            value={formData.end_date || ''}
                            onChange={handleChange}
                            disabled={!!formData.duration_days}
                        />
                        {formData.duration_days && (
                            <p className="text-xs text-gray-500">Disabled because Duration is set</p>
                        )}
                    </div>

                    <div className="flex flex-row gap-4">
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                name="auto_renewal"
                                checked={formData.auto_renewal || false}
                                onChange={handleChange}
                                className="w-4 h-4 text-orange-500 focus:ring-orange-500 border-gray-300 rounded"
                            />
                            <label className="text-sm font-medium">Auto Renewal</label>
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                name="isActive"
                                checked={formData.isActive !== undefined ? formData.isActive : true}
                                onChange={handleChange}
                                className="w-4 h-4 text-orange-500 focus:ring-orange-500 border-gray-300 rounded"
                            />
                            <label className="text-sm font-medium">Active</label>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 text-sm text-white rounded-md bg-orange hover:bg-opacity-90 transition-colors duration-200"
                        >
                            {isEdit ? 'Update' : 'Create'} Subscription Type
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const SubscriptionTypesPage = () => {
    const [subscriptionTypes, setSubscriptionTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedSubscriptionType, setSelectedSubscriptionType] = useState(null);

    // Search and Filter states
    const [searchTerm, setSearchTerm] = useState("");
    const [currencyFilter, setCurrencyFilter] = useState("all");
    const [frequencyFilter, setFrequencyFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");

    // Sorting states
    const [sortField, setSortField] = useState("subscription_name");
    const [sortDirection, setSortDirection] = useState("asc");

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const [formData, setFormData] = useState({
        subscription_name: '',
        description: '',
        billing_frequency: '',
        price: '',
        currency: '',
        duration_days: '',
        auto_renewal: true,
        features: '',
        isActive: true,
        end_date: ''
    });

    useEffect(() => {
        fetchSubscriptionTypes();
    }, []);

    // Helper function to convert datetime-local input to SGT timezone format
    const formatToSGTTimezone = (datetimeLocal) => {
        if (!datetimeLocal) return null;
        // datetime-local format: "2025-06-30T23:59"
        // We treat this as SGT time and add the timezone offset
        return `${datetimeLocal}:00+08:00`;
    };

    // Helper function to display date in consistent SGT format
    const formatDisplayDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        // Format as "Jun 30, 2025 11:59 PM SGT"
        return date.toLocaleString('en-US', {
            timeZone: 'Asia/Singapore',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        }) + ' SGT';
    };

    const fetchSubscriptionTypes = async () => {
        try {
            setLoading(true);
            const response = await getAllSubscriptionTypes();
            if (response && Array.isArray(response.Data)) {
                setSubscriptionTypes(response.Data);
            } else {
                setError("Received data structure is not as expected");
            }
        } catch (error) {
            console.error("Error fetching subscription types:", error);
            setError(error.message || "An error occurred while fetching subscription types");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateSubscriptionType = async (e) => {
        e.preventDefault();

        // Validate that either duration_days or end_date is provided
        if (!formData.duration_days && !formData.end_date) {
            alert('Please provide either Duration (Days) or End Date');
            return;
        }

        try {
            const submitData = { ...formData };
            // Convert features string to array
            if (typeof submitData.features === 'string') {
                submitData.features = submitData.features.split(',').map(item => item.trim()).filter(item => item);
            }
            // Set empty duration_days or end_date to null
            if (!submitData.duration_days) {
                submitData.duration_days = null;
            }
            if (!submitData.end_date) {
                submitData.end_date = null;
            } else {
                // Convert datetime-local to SGT timezone format
                submitData.end_date = formatToSGTTimezone(submitData.end_date);
            }
            const response = await createSubscriptionType(submitData);
            if (response.status_code === 200) {
                alert(response.message);
                setShowCreateModal(false);
                resetForm();
                fetchSubscriptionTypes();
            } else {
                alert(response.message || 'Failed to create subscription type');
            }
        } catch (error) {
            alert(error.response?.data?.message || error.message || 'Failed to create subscription type');
        }
    };

    const handleUpdateSubscriptionType = async (e) => {
        e.preventDefault();
        if (!formData.duration_days && !formData.end_date) {
            alert('Please provide either Duration (Days) or End Date');
            return;
        }
        try {
            const submitData = { ...formData };
            if (typeof submitData.features === 'string') {
                submitData.features = submitData.features.split(',').map(item => item.trim()).filter(item => item);
            }
            if (!submitData.duration_days) {
                submitData.duration_days = null;
            }
            if (!submitData.end_date) {
                submitData.end_date = null;
            } else {
                // Convert datetime-local to SGT timezone format
                submitData.end_date = formatToSGTTimezone(submitData.end_date);
            }
            const response = await updateSubscriptionType(selectedSubscriptionType._id, submitData);
            console.log(response)
            console.log(submitData)
            if (response["Base Output"]?.status_code === 200) {
                alert(response["Base Output"].message);
                setShowEditModal(false);
                setSelectedSubscriptionType(null);
                resetForm();
                fetchSubscriptionTypes();
            } else {
                alert(response?.message || 'Failed to update subscription type');
            }
        } catch (error) {
            alert(error.response?.data?.message || error.message || 'Failed to update subscription type');
        }
    };

    const handleDeleteSubscriptionType = async (subscriptionTypeId) => {
        if (window.confirm('Are you sure you want to delete this subscription type?')) {
            try {
                const response = await deleteSubscriptionType(subscriptionTypeId);
                if (response.status_code === 200) {
                    alert('Subscription type deleted successfully');
                    fetchSubscriptionTypes();
                } else {
                    alert(response.message || 'Failed to delete subscription type');
                }
            } catch (error) {
                alert(error.response?.data?.message || error.message || 'Failed to delete subscription type');
            }
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let updateData = {
            [name]: type === 'checkbox' ? checked : value
        };

        // Make end_date and duration_days mutually exclusive
        if (name === 'end_date' && value) {
            updateData.duration_days = '';
        } else if (name === 'duration_days' && value) {
            updateData.end_date = '';
        }

        setFormData(prevState => ({
            ...prevState,
            ...updateData
        }));
    };

    const resetForm = () => {
        setFormData({
            subscription_name: '',
            description: '',
            billing_frequency: '',
            price: '',
            currency: '',
            duration_days: '',
            auto_renewal: true,
            features: '',
            isActive: true,
            end_date: ''
        });
    };

    const openCreateModal = () => {
        resetForm();
        setShowCreateModal(true);
    };

    const openEditModal = (subscriptionType) => {
        setSelectedSubscriptionType(subscriptionType);

        // Helper function to format SGT timezone date for datetime-local input
        const formatDateTimeLocal = (dateString) => {
            if (!dateString) return '';
            // Parse the date and convert to SGT timezone
            const date = new Date(dateString);
            // Convert to SGT timezone for the datetime-local input
            const sgtDate = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Singapore' }));
            // Format as YYYY-MM-DDTHH:MM for datetime-local input
            const year = sgtDate.getFullYear();
            const month = String(sgtDate.getMonth() + 1).padStart(2, '0');
            const day = String(sgtDate.getDate()).padStart(2, '0');
            const hours = String(sgtDate.getHours()).padStart(2, '0');
            const minutes = String(sgtDate.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
        };

        setFormData({
            subscription_name: subscriptionType.subscription_name,
            description: subscriptionType.description,
            billing_frequency: subscriptionType.billing_frequency,
            price: subscriptionType.price,
            currency: subscriptionType.currency,
            duration_days: subscriptionType.duration_days,
            auto_renewal: subscriptionType.auto_renewal,
            features: Array.isArray(subscriptionType.features) ? subscriptionType.features.join(', ') : subscriptionType.features || '',
            isActive: subscriptionType.isActive !== undefined ? subscriptionType.isActive : true,
            end_date: formatDateTimeLocal(subscriptionType.end_date)
        });
        setShowEditModal(true);
    };

    const closeModals = () => {
        setShowCreateModal(false);
        setShowEditModal(false);
        setSelectedSubscriptionType(null);
        resetForm();
    };

    // Sorting function
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
        setCurrentPage(1);
    };

    // Filter and sort subscription types
    const filteredSubscriptionTypes = subscriptionTypes.filter(type => {
        const matchesSearch = type.subscription_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            type.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCurrency = currencyFilter === "all" || type.currency === currencyFilter;
        const matchesFrequency = frequencyFilter === "all" || type.billing_frequency === frequencyFilter;
        const matchesStatus = statusFilter === "all" ||
            (statusFilter === "active" && type.isActive) ||
            (statusFilter === "inactive" && !type.isActive);

        return matchesSearch && matchesCurrency && matchesFrequency && matchesStatus;
    });

    const sortedSubscriptionTypes = [...filteredSubscriptionTypes].sort((a, b) => {
        let aValue, bValue;

        switch (sortField) {
            case 'subscription_name':
                aValue = a.subscription_name.toLowerCase();
                bValue = b.subscription_name.toLowerCase();
                break;
            case 'price':
                aValue = parseFloat(a.price);
                bValue = parseFloat(b.price);
                break;
            case 'currency':
                aValue = a.currency.toLowerCase();
                bValue = b.currency.toLowerCase();
                break;
            case 'billing_frequency':
                aValue = a.billing_frequency?.toLowerCase() || '';
                bValue = b.billing_frequency?.toLowerCase() || '';
                break;
            case 'duration_days':
                aValue = parseInt(a.duration_days);
                bValue = parseInt(b.duration_days);
                break;
            case 'isActive':
                aValue = a.isActive ? 1 : 0;
                bValue = b.isActive ? 1 : 0;
                break;
            case 'end_date':
                aValue = a.end_date ? new Date(a.end_date) : new Date(0);
                bValue = b.end_date ? new Date(b.end_date) : new Date(0);
                break;
            default:
                aValue = a.subscription_name.toLowerCase();
                bValue = b.subscription_name.toLowerCase();
                break;
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    // Pagination
    const totalPages = Math.ceil(sortedSubscriptionTypes.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedSubscriptionTypes = sortedSubscriptionTypes.slice(startIndex, startIndex + itemsPerPage);

    // Pagination controls
    const goToPage = (page) => {
        setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    };

    const SortableHeader = ({ field, children, align = 'left' }) => (
        <th
            className={`px-6 py-4 text-${align} text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-200`}
            onClick={() => handleSort(field)}
        >
            <div className="flex items-center space-x-1">
                <span>{children}</span>
                <div className="flex flex-col">
                    <ChevronUpIcon
                        className={`w-3 h-3 ${sortField === field && sortDirection === 'asc' ? 'text-orange-500' : 'text-gray-400'}`}
                    />
                    <ChevronDownIcon
                        className={`w-3 h-3 -mt-1 ${sortField === field && sortDirection === 'desc' ? 'text-orange-500' : 'text-gray-400'}`}
                    />
                </div>
            </div>
        </th>
    );

    return (
        <div className="mb-8">
            <div className="flex justify-between items-center mb-6">
                <button
                    onClick={openCreateModal}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-white rounded-md bg-orange hover:bg-opacity-90 transition-colors duration-200"
                >
                    <PlusIcon className="w-4 h-4" />
                    Create Subscription Type
                </button>
            </div>

            {/* Filters */}
            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">Search</label>
                    <input
                        type="text"
                        placeholder="Search by name or description..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">Currency</label>
                    <select
                        value={currencyFilter}
                        onChange={(e) => {
                            setCurrencyFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                        <option value="all">All Currencies</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                        <option value="SGD">SGD</option>
                        <option value="IDR">IDR</option>
                    </select>
                </div>

                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">Billing Frequency</label>
                    <select
                        value={frequencyFilter}
                        onChange={(e) => {
                            setFrequencyFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                        <option value="all">All Frequencies</option>
                        <option value="once">Once</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="annual">Annual</option>
                    </select>
                </div>

                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">Status</label>
                    <select
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>
                </div>

                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">Items per page</label>
                    <select
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                    </select>
                </div>
            </div>

            {/* Subscription Types Table */}
            {loading ? (
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
                </div>
            ) : error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-700">Error: {error}</p>
                </div>
            ) : (
                <>
                    {/* Data Count Info */}
                    <div className="mb-4 flex items-center justify-between bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">
                        <div className="flex items-center space-x-4">
                            <div className="text-sm text-gray-700">
                                <span className="font-medium text-gray-900">{filteredSubscriptionTypes.length}</span>
                                {filteredSubscriptionTypes.length === 1 ? ' subscription type' : ' subscription types'} found
                                {(searchTerm || currencyFilter !== 'all' || frequencyFilter !== 'all' || statusFilter !== 'all') && (
                                    <span className="text-gray-500"> (filtered from {subscriptionTypes.length} total)</span>
                                )}
                            </div>
                            {(searchTerm || currencyFilter !== 'all' || frequencyFilter !== 'all' || statusFilter !== 'all') && (
                                <div className="flex items-center space-x-2">
                                    {searchTerm && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            Search: "{searchTerm}"
                                        </span>
                                    )}
                                    {currencyFilter !== 'all' && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                            Currency: {currencyFilter}
                                        </span>
                                    )}
                                    {frequencyFilter !== 'all' && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            Frequency: {frequencyFilter}
                                        </span>
                                    )}
                                    {statusFilter !== 'all' && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                            Status: {statusFilter}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        {(searchTerm || currencyFilter !== 'all' || frequencyFilter !== 'all' || statusFilter !== 'all') && (
                            <button
                                onClick={() => {
                                    setSearchTerm('');
                                    setCurrencyFilter('all');
                                    setFrequencyFilter('all');
                                    setStatusFilter('all');
                                    setCurrentPage(1);
                                }}
                                className="text-sm text-gray-500 hover:text-gray-700 underline"
                            >
                                Clear all filters
                            </button>
                        )}
                    </div>

                    <div className="bg-white shadow-lg rounded-lg overflow-hidden border border-gray-200">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <SortableHeader field="subscription_name">Name</SortableHeader>
                                        <SortableHeader field="price">Price</SortableHeader>
                                        <SortableHeader field="currency">Currency</SortableHeader>
                                        <SortableHeader field="billing_frequency">Frequency</SortableHeader>
                                        <SortableHeader field="duration_days">Duration</SortableHeader>
                                        <SortableHeader field="end_date">End Date</SortableHeader>
                                        <SortableHeader field="isActive">Status</SortableHeader>
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Auto Renewal
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {paginatedSubscriptionTypes.length > 0 ? (
                                        paginatedSubscriptionTypes.map((type, index) => (
                                            <tr key={type._id || `type-${index}`} className="hover:bg-gray-50 transition-colors duration-200">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex flex-col">
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {type.subscription_name}
                                                        </div>
                                                        <div className="text-sm text-gray-500 truncate max-w-xs">
                                                            {type.description}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                                    {type.price}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {type.currency}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${type.billing_frequency === 'once' ? 'bg-gray-100 text-gray-800' :
                                                        type.billing_frequency === 'monthly' ? 'bg-blue-100 text-blue-800' :
                                                            type.billing_frequency === 'quarterly' ? 'bg-purple-100 text-purple-800' :
                                                                type.billing_frequency === 'annual' ? 'bg-green-100 text-green-800' :
                                                                    'bg-yellow-100 text-yellow-800'
                                                        }`}>
                                                        {type.billing_frequency?.charAt(0).toUpperCase() + type.billing_frequency?.slice(1) || 'N/A'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {type.duration_days ? `${type.duration_days} days` : (type.end_date ? 'Set by End Date' : 'N/A')}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {type.end_date ? formatDisplayDate(type.end_date) : (type.duration_days ? 'Set by Duration' : 'N/A')}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${type.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                        }`}>
                                                        {type.isActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${type.auto_renewal ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                        }`}>
                                                        {type.auto_renewal ? 'Yes' : 'No'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <div className="flex items-center space-x-3">
                                                        <button
                                                            onClick={() => openEditModal(type)}
                                                            className="text-orange-600 hover:text-orange-900 transition-colors duration-200"
                                                            title="Edit Subscription Type"
                                                        >
                                                            <PencilIcon className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteSubscriptionType(type._id)}
                                                            className="text-red-600 hover:text-red-900 transition-colors duration-200"
                                                            title="Delete Subscription Type"
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                                                <div className="flex flex-col items-center">
                                                    <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                    <p className="text-lg font-medium">No subscription types found</p>
                                                    <p className="text-sm">Try adjusting your search or filter criteria</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4 rounded-lg shadow">
                            <div className="flex-1 flex justify-between sm:hidden">
                                <button
                                    onClick={() => goToPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => goToPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-sm text-gray-700">
                                        Showing{' '}
                                        <span className="font-medium">{startIndex + 1}</span>
                                        {' '}to{' '}
                                        <span className="font-medium">
                                            {Math.min(startIndex + itemsPerPage, sortedSubscriptionTypes.length)}
                                        </span>
                                        {' '}of{' '}
                                        <span className="font-medium">{sortedSubscriptionTypes.length}</span>
                                        {' '}results
                                    </p>
                                </div>
                                <div>
                                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                        <button
                                            onClick={() => goToPage(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <ChevronLeftIcon className="h-5 w-5" />
                                        </button>

                                        {/* Page numbers */}
                                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                            let pageNum;

                                            if (totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 3) {
                                                pageNum = i + 1;
                                            } else if (currentPage >= totalPages - 2) {
                                                pageNum = totalPages - 4 + i;
                                            } else {
                                                pageNum = currentPage - 2 + i;
                                            }

                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => goToPage(pageNum)}
                                                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNum
                                                        ? 'z-10 bg-orange-500 border-orange-500 text-white'
                                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                        }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}

                                        <button
                                            onClick={() => goToPage(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <ChevronRightIcon className="h-5 w-5" />
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Create Subscription Type Modal */}
            <SubscriptionTypeModal
                isOpen={showCreateModal}
                onClose={closeModals}
                onSubmit={handleCreateSubscriptionType}
                title="Create New Subscription Type"
                formData={formData}
                handleChange={handleChange}
            />

            {/* Edit Subscription Type Modal */}
            <SubscriptionTypeModal
                isOpen={showEditModal}
                onClose={closeModals}
                onSubmit={handleUpdateSubscriptionType}
                title="Edit Subscription Type"
                isEdit={true}
                formData={formData}
                handleChange={handleChange}
            />
        </div>
    );
};

export default SubscriptionTypesPage;
